import type { CurationRunOutcomes, CurationRunStats } from '../types/curation.api.types';
import type { RunProgress, StageProgress } from '../types/run.api.types';

import type { LedgerOutcomes } from './run-stage-facts';

/** One counter of a stage's funnel, ready for a labelled definition list. */
export interface FunnelCounter {
  key: string;
  value: number;
}

/** The three steps of a run's product flow, in the order they happen (3.4). */
export interface CurationFunnelStory {
  /** Step ① — what the sync read (feed rows) and how much of it was already known. */
  sync: {
    rows: number;
    /** Rows already in the catalog (`existing`). */
    existing: number;
    /** Rows set aside by filters/scope or as already curated (`filtered`). */
    setAside: number;
    /** What `setAside` is made of (`setAsideBreakdown`). */
    setAsideParts: SetAsideParts;
  } | null;
  /** Step ② — the experiment's arm split, from ARM_ASSIGNMENT (succeeded = test, skipped = control). */
  split: { test: number; control: number } | null;
  /** Step ③ — what curation did with the rows it was handed. */
  curation: {
    /** Rows handed to curation (`products_expected`). */
    rows: number;
    curated: number;
    /** Rows not curated and not failed — flagged as needing attention. */
    heldBack: number;
    failed: number;
    /** New catalog PRODUCTS (not rows). */
    newProducts: number | null;
    /** Distinct catalog PRODUCTS the curated rows resolved to (`funnel.products`); null = not reported. */
    products: number | null;
    /** Stage status (PENDING / RUNNING / COMPLETED / …). */
    status: string;
  } | null;
  /** IMPORT's own funnel counters (feed rows) for "Details ▸". */
  importCounters: FunnelCounter[];
  /** CURATION's own funnel counters for "Details ▸". */
  curationCounters: FunnelCounter[];
}

/**
 * What the sync's set-aside rows (`filtered`) were. `existing_skipped` = already-curated rows
 * left alone: unchanged ones plus offer-only ones (`offer_updated` — only price / availability
 * changed) that owed no re-curation. An offer-only row that DID owe curation counts in
 * `changed`, so the settled offer-only share is exact only when its bounds meet; otherwise
 * `offerOnly` / `unchanged` are null and the rows read as "already curated" together.
 */
export interface SetAsideParts {
  /** Offer-only updates (price / availability) left as they were; null = not separable. */
  offerOnly: number | null;
  /** Already curated and unchanged; null = not separable from `offerOnly`. */
  unchanged: number | null;
  /** `existing_skipped` — the two above together. */
  alreadyCurated: number;
  /** Rows the trigger's filters excluded (`excluded_by_filters`). */
  excluded: number;
  /** Anything else `filtered` holds (duplicates, scope) — the remainder, never negative. */
  other: number;
}

/** Splits IMPORT's `filtered` into what it was (see `SetAsideParts`). */
export function setAsideBreakdown(funnel: Record<string, number> | undefined): SetAsideParts {
  const alreadyCurated = funnel?.existing_skipped ?? 0;
  const excluded = funnel?.excluded_by_filters ?? 0;
  const offer = funnel?.offer_updated ?? 0;
  // offer_updated = settled + curated; the curated share is at most `changed`.
  const low = Math.max(0, offer - (funnel?.changed ?? 0));
  const high = Math.min(offer, alreadyCurated);
  const exact = low === high;
  return {
    offerOnly: exact ? high : null,
    unchanged: exact ? alreadyCurated - high : null,
    alreadyCurated,
    excluded,
    other: Math.max(0, (funnel?.filtered ?? 0) - alreadyCurated - excluded),
  };
}

/** The import funnel keys in pipeline order; anything else follows, alphabetically. */
const IMPORT_ORDER = [
  'extracted',
  'filtered',
  'excluded_by_filters',
  'validated',
  'invalid',
  'matched',
  'existing',
  'existing_skipped',
  'loaded',
  'skipped',
  'errors',
];

/** The curation stage's funnel keys in the order a person reads them. */
const CURATION_ORDER = ['curated', 'products', 'matched', 'new', 'reused'];

/** The newest attempt of one stage, or null when the flow never declared it. */
export function latestStage(stages: StageProgress[], stage: string): StageProgress | null {
  return stages
    .filter((s) => s.stage === stage)
    .reduce<StageProgress | null>((acc, s) => (!acc || s.attempt > acc.attempt ? s : acc), null);
}

/** One snake_case stage counter, 0 when the stage did not report it. */
function count(stage: StageProgress, key: string): number {
  return stage.counts?.[key] ?? 0;
}

/**
 * Orders a funnel map for display: known keys first in pipeline order, then the rest. Zero
 * counters are dropped except `keep` — a definition list of zeros explains nothing (Q14).
 */
function orderCounters(
  funnel: Record<string, number> | undefined,
  order: string[],
  keep: string[],
): FunnelCounter[] {
  if (!funnel) return [];
  const known = order.filter((k) => k in funnel);
  const rest = Object.keys(funnel)
    .filter((k) => !order.includes(k))
    .sort();
  return [...known, ...rest]
    .map((key) => ({ key, value: funnel[key] ?? 0 }))
    .filter((c) => c.value > 0 || keep.includes(c.key));
}

/**
 * 3.4 — turns the run's progress + curation reads into the product-flow story ("Sync 40 rows (all
 * already in catalog) → split 14 test · 26 control → curation of 14: 13 curated · 1 held back · 0
 * new products"). Every number comes from the API: IMPORT's funnel (`extracted` = sync size,
 * `filtered` = set aside, `existing` = already in catalog), ARM_ASSIGNMENT's counts (succeeded =
 * test arm, skipped = control) and CURATION's counts (expected / succeeded / failed). When the
 * CURATION counters were never written, `ledger` (the run's own `run_items`, newest attempt) fills
 * them, so the card never reads zero for a run whose listings later runs took over.
 */
export function buildCurationFunnelStory(
  progress: RunProgress | undefined,
  stats: CurationRunStats | undefined,
  outcomes: CurationRunOutcomes | undefined,
  ledger?: LedgerOutcomes | null,
): CurationFunnelStory {
  const stages = progress?.stages ?? [];
  const importStage = latestStage(stages, 'IMPORT');
  const armStage = latestStage(stages, 'ARM_ASSIGNMENT');
  const curationStage = latestStage(stages, 'CURATION');

  // IMPORT's own attempt funnel is the sync; fall back to the run-wide curation counters.
  const importFunnel =
    importStage?.funnelCounts && Object.keys(importStage.funnelCounts).length > 0
      ? importStage.funnelCounts
      : (stats?.funnel ?? progress?.funnel);
  const extracted = importFunnel?.extracted;

  const sync =
    extracted !== undefined
      ? {
          rows: extracted,
          existing: importFunnel?.existing ?? 0,
          setAside: importFunnel?.filtered ?? 0,
          setAsideParts: setAsideBreakdown(importFunnel),
        }
      : null;

  const split =
    armStage?.status === 'COMPLETED'
      ? {
          test: count(armStage, 'products_succeeded'),
          control: count(armStage, 'products_skipped'),
        }
      : null;

  const curation = curationStage
    ? (() => {
        const unwritten =
          count(curationStage, 'products_expected') === 0 &&
          count(curationStage, 'products_succeeded') === 0;
        const fromLedger = unwritten && !!ledger;
        const rows = fromLedger ? ledger.total : count(curationStage, 'products_expected');
        const curated = fromLedger ? ledger.success : count(curationStage, 'products_succeeded');
        const failed = fromLedger ? ledger.failed : count(curationStage, 'products_failed');
        const skipped = fromLedger ? ledger.skipped : count(curationStage, 'products_skipped');
        return {
          rows,
          curated,
          failed,
          // Expected rows that neither curated, failed nor were skipped were held back (flagged).
          heldBack: Math.max(0, rows - curated - failed - skipped),
          newProducts: curationStage.funnelCounts?.new ?? outcomes?.newProducts ?? null,
          products: curationStage.funnelCounts?.products ?? null,
          status: curationStage.status,
        };
      })()
    : null;

  return {
    sync,
    split,
    curation,
    importCounters: orderCounters(importFunnel, IMPORT_ORDER, ['extracted']),
    curationCounters: orderCounters(curationStage?.funnelCounts, CURATION_ORDER, []),
  };
}

/**
 * Distinct catalog PRODUCTS this run's curation resolved to (`CURATION.funnelCounts.products`,
 * the same number the "This batch" rail's "Products" line shows) — null when the backend hasn't
 * reported it. `useCurationRunProducts` returns one row per LISTING (a market chip each), so any
 * screen that counts those rows must say "listings", not "products", unless it also has this.
 */
export function curationDistinctProducts(progress: RunProgress | undefined): number | null {
  return latestStage(progress?.stages ?? [], 'CURATION')?.funnelCounts?.products ?? null;
}
